import {Component, ElementRef, EventEmitter, Inject, Input, OnInit, Output, ViewChild} from '@angular/core';
import {isString, merge} from 'lodash-es';
import {ApiResponse} from "../../../classes/api-response";
import {SnackService} from "../../../services/snack.service";
import {Box} from "../../../model/box";
import {UsersService} from "../../../services/users.service";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {MatAutocomplete} from '@angular/material/autocomplete';
import {ContractsService} from 'src/app/services/contracts.service';
import FuzzySearch from 'fuzzy-search';
import {hasPermission, User} from "../../../model/user";
import {Constants} from "../../../utils/constants";

@Component({
  selector: 'aw-add-researchers-to-box',
  templateUrl: './add-researchers-to-box.component.html',
  styleUrls: ['./add-researchers-to-box.component.scss']
})
export class AddResearchersToBoxComponent implements OnInit {

  @Output() saved = new EventEmitter<void>();
  @Output() canceled = new EventEmitter<void>();
  loadingResearchers = false;
  saving = false;
  generalError?: ApiResponse;

  selectedResearchers: User[] = [];
  filteredResearchers: User[] = [];
  allPossibleResearchers: User[] = [];
  @Input() inputString = '';
  @ViewChild('in') inputField: ElementRef;
  @ViewChild('autocomplete') autocompleteField: MatAutocomplete;
  private fuzzySearcher: FuzzySearch<User>;

  constructor(
    private snackService: SnackService,
    private usersService: UsersService,
    private contractService: ContractsService,
    @Inject(MAT_DIALOG_DATA) public injectedBox: Box,
  ) {
    this.loadingResearchers = true;

    this.usersService.getUsers().subscribe(researchers => {
      if (injectedBox.researchers) {
        const ids = injectedBox.researchers.map(researcher => researcher.id);
        researchers = researchers.filter((researcher) => !ids.includes(researcher.id));
      }

      // fixme: it may be a weak check:
      //  api now returns:
      //  - all users if loggedUser is a admin
      //  - all researchers and testers  if loggedUser is a customer
      researchers = researchers
        .filter((researcher) => hasPermission(researcher,"researcher"));

      this.allPossibleResearchers = researchers;


      this.fuzzySearcher = new FuzzySearch(this.allPossibleResearchers, ["firstName", "lastName", "email"], {
        caseSensitive: false,
        sort: true
      });
      this.sortAlphabeticallyByLastName(this.allPossibleResearchers);
      this.filteredResearchers = researchers;
      this.loadingResearchers = false;
      if (!this._box.id) throw Error("There was a problem with the injection of the box");
      contractService.getContractByBox(this._box.id).subscribe(contractRetrieved => {
        this._box.contract = contractRetrieved;
      });
    });
  }

  _box = new Box();

  @Input() set box(value: Box) {
    this._box = merge(new Box(), value);
  }

  showFilteredData(): void {
    this.filteredResearchers = this.allPossibleResearchers;
    if (isString(this.inputString)) {
      let string: string = this.inputString.trim();
      string = this.inputString.toLowerCase();
      const separatedString: string[] = string.split(' ');
      const researcherSet: Set<User> = new Set<User>();
      separatedString.forEach(substring => {
        const subSet = this.fuzzySearcher.search(substring);
        subSet.forEach(researcher => {
          researcherSet.add(researcher);
        });
      });
      this.filteredResearchers = Array.from(researcherSet);
    }
  }

  onOptionClicked(researcher: User): void {
    if (researcher) {
      if (!this.selectedResearchers.includes(researcher)) {
        this.selectedResearchers.push(researcher);
        this.allPossibleResearchers.splice(this.allPossibleResearchers.indexOf(researcher), 1);
        this.resetInput();
      } else this.snackService.error("User already selected");
    }
  }

  removeChip(researcher: User): void {
    this.selectedResearchers.splice(this.selectedResearchers.indexOf(researcher), 1);
    this.allPossibleResearchers.push(researcher);
    this.sortAlphabeticallyByLastName(this.allPossibleResearchers);
    this.showFilteredData();
  }

  resetInput(): void {
    this.inputField.nativeElement.value = '';
    this.inputString = "";
    this.showFilteredData();
    this.inputField.nativeElement.blur();
    setTimeout(() => {
      this.inputField.nativeElement.focus();
    }, 0);
  }

  ngOnInit(): void {
  }

  addResearchers(): void {
    this.saving = true;
    if (!this._box.contract) throw Error("Could not load contract from the backend: check your internet connection and privileges");
    // TODO: API that may have changed
    this.usersService.assignResearchersToBox(this.selectedResearchers, this._box.id, this._box.contract?.id)
      .subscribe(
        () => {
          this.saving = false;
          if (this.selectedResearchers)
            this.selectedResearchers.forEach((researcher: User) => {
              this.injectedBox.researchers?.push(researcher);
            });
          this.snackService.success('Researchers associated!');
          this.saved.emit();
        },
        error => {
          this.saving = false;
          this.snackService.error(error.message ?? "Error while assigning researchers!");
          this.generalError = error;
        });
  }

  private sortAlphabeticallyByLastName(arr: User[]) {
    arr.sort((a, b) => {
      if (a && b && a.lastName && b.lastName) {
        return a.lastName.toLowerCase().localeCompare(b.lastName.toLowerCase());
      } else
        return 0;
    });
  }
}
